// src/pages/AdminEquipements.jsx
import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import Button from '../components/Button';
import { listEntites } from '../api/salles';
import {
  listEquipements,
  createEquipement,
  updateEquipement,
  deleteEquipement,
} from '../api/equipements';
import toast from 'react-hot-toast';
import { Edit, CirclePlus, ChevronDown, ChevronRight, Trash2, LockKeyhole, Wifi } from 'lucide-react';
import { createPortal } from 'react-dom';
import { CONFIG } from '../config/config';
import TYPES_EQUIPEMENTS, { TYPES_HOME_ASSISTANT, TYPES_AVEC_IP } from '../data/typesEquipements';

const API_URL = (CONFIG.API_URL || '/api').replace(/\/+$/, '');

const ROLES_DISPONIBLES = [
  { value: 'secretaire', label: 'Secrétaire' },
  { value: 'infirmiere', label: 'Infirmière' },
  { value: 'medecin', label: 'Médecin' },
  { value: 'admin', label: 'Admin' },
];

const INPUT = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300';
const LABEL = 'block text-xs font-medium text-gray-600 mb-1';

function ChampsCommuns({ form, onChange }) {
  return (
    <>
      <div>
        <label className={LABEL}>Libellé *</label>
        <input
          className={INPUT}
          placeholder="ex: Armoire salle 2"
          value={form.libelle || ''}
          onChange={(e) => onChange('libelle', e.target.value)}
        />
      </div>
      <div>
        <label className={LABEL}>Type *</label>
        <select
          className={INPUT}
          value={form.type || ''}
          onChange={(e) => onChange('type', e.target.value)}
        >
          <option value="">-</option>
          {TYPES_EQUIPEMENTS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
    </>
  );
}

function ChampsIP({ form, onChange }) {
  const isZebra = form.type === 'imprimante-code-barre';
  const cfg = form.config || {};
  const set = (key, val) => onChange('config', { ...cfg, [key]: val });

  return (
    <>
      <div className="col-span-full">
        <label className={LABEL}>Adresse IP *</label>
        <input
          className={INPUT}
          placeholder="ex: 172.16.1.90"
          value={form.ip || ''}
          onChange={(e) => onChange('ip', e.target.value)}
        />
      </div>
      {isZebra && (
        <div className="col-span-full">
          <label className={LABEL}>Port ZPL <span className="text-gray-400 font-normal">(défaut : 9100)</span></label>
          <input
            className={INPUT}
            placeholder="9100"
            type="number"
            min="1"
            max="65535"
            value={cfg.port || ''}
            onChange={(e) => set('port', e.target.value ? Number(e.target.value) : undefined)}
          />
        </div>
      )}
    </>
  );
}

function ChampsArmoireHA({ form, onChange }) {
  const cfg = form.config || {};
  const set = (key, val) => onChange('config', { ...cfg, [key]: val });
  const roles = Array.isArray(cfg.roles) ? cfg.roles : ['infirmiere', 'medecin', 'admin'];

  return (
    <div className="col-span-full space-y-3 border-t border-amber-200 pt-3 mt-1 bg-amber-50/40 rounded-b-lg px-1">
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
        <LockKeyhole size={13} />
        Le token Home Assistant est stocké côté serveur uniquement — jamais transmis au navigateur.
      </p>

      <div>
        <label className={LABEL}>URL du service Home Assistant *</label>
        <input
          className={INPUT}
          placeholder="http://172.16.1.90:8123/api/services/input_button/press"
          value={cfg.ha_url || ''}
          onChange={(e) => set('ha_url', e.target.value)}
        />
      </div>

      <div>
        <label className={LABEL}>Token d'autorisation HA *</label>
        <input
          className={INPUT}
          type="password"
          placeholder="eyJhbGci… (Long-Lived Access Token)"
          value={cfg.ha_token || ''}
          onChange={(e) => set('ha_token', e.target.value)}
        />
      </div>

      <div>
        <label className={LABEL}>Entity ID *</label>
        <input
          className={INPUT}
          placeholder="input_button.armoire_medicale_salle2"
          value={cfg.ha_entity_id || ''}
          onChange={(e) => set('ha_entity_id', e.target.value)}
        />
      </div>

      <div>
        <label className={LABEL}>Rôles autorisés à utiliser le bouton</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {ROLES_DISPONIBLES.map((r) => {
            const checked = roles.includes(r.value);
            return (
              <label
                key={r.value}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition
                  ${checked ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'bg-white border-gray-200 text-gray-500'}`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => set('roles', checked ? roles.filter((x) => x !== r.value) : [...roles, r.value])}
                />
                {r.label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EquipementForm({ form, onChange }) {
  const isHA = TYPES_HOME_ASSISTANT.includes(form.type);
  const hasIP = TYPES_AVEC_IP.includes(form.type);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <ChampsCommuns form={form} onChange={onChange} />
      {hasIP && <ChampsIP form={form} onChange={onChange} />}
      {isHA && <ChampsArmoireHA form={form} onChange={onChange} />}
    </div>
  );
}

function Modal({ open, onClose, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1002] flex items-center justify-center bg-black/40 p-4">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-xl p-5">
        {children}
      </div>
    </div>,
    document.body,
  );
}

function TypeBadge({ type }) {
  const isHA = TYPES_HOME_ASSISTANT.includes(type);
  const label = TYPES_EQUIPEMENTS.find((t) => t.value === type)?.label || type;
  return (
    <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-medium
      ${isHA ? 'bg-amber-900 text-amber-100' : 'bg-slate-900 text-slate-50'}`}>
      {isHA && <LockKeyhole size={10} />}
      {label}
    </span>
  );
}

export default function AdminEquipements() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entities, setEntities] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [openIds, setOpenIds] = useState(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState({});
  const [testingId, setTestingId] = useState(null);

  const testConnection = async (eq) => {
    setTestingId(eq.id);
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/print/test-connection/${eq.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(`✅ Connexion OK — ${data.host}:${data.port}`);
      } else {
        toast.error(`❌ ${data.error || 'Échec connexion'}`);
      }
    } catch (e) {
      toast.error(`❌ Erreur réseau: ${e.message}`);
    } finally {
      setTestingId(null);
    }
  };

  function toggle(id) {
    setOpenIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const getEqs = (etab) => equipements.filter((e) => e.etablissement_id === etab.etablissement_id);

  const refresh = async () => {
    setError(''); setLoading(true);
    try {
      const [ents, eqs] = await Promise.all([listEntites(), listEquipements()]);
      setEntities(ents); setEquipements(eqs);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const changeCreate = (k, v) => setCreateForm((f) => ({ ...f, [k]: v }));
  const changeUpdate = (k, v) => setUpdateForm((f) => ({ ...f, [k]: v }));

  const onCreateEquipement = async (e) => {
    e.preventDefault();
    try {
      if (!createForm.libelle?.trim()) throw new Error('Libellé requis');
      if (!createForm.type?.trim()) throw new Error('Type requis');
      if (!createForm.etablissement_id) throw new Error('Établissement requis');
      if (TYPES_AVEC_IP.includes(createForm.type) && !createForm.ip?.trim()) throw new Error('IP requise');
      if (TYPES_HOME_ASSISTANT.includes(createForm.type)) {
        const cfg = createForm.config || {};
        if (!cfg.ha_url?.trim()) throw new Error('URL Home Assistant requise');
        if (!cfg.ha_token?.trim()) throw new Error('Token HA requis');
        if (!cfg.ha_entity_id?.trim()) throw new Error('Entity ID requis');
      }
      await createEquipement(createForm);
      toast.success('Équipement créé');
      setCreateOpen(false); setCreateForm({});
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  const onUpdateEquipement = async (e) => {
    e.preventDefault();
    try {
      if (!updateForm.libelle?.trim()) throw new Error('Libellé requis');
      await updateEquipement(updateForm.id, updateForm);
      toast.success('Équipement modifié');
      setUpdateOpen(false); setUpdateForm({});
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  const onDeleteEquipement = async () => {
    try {
      await deleteEquipement(deleteForm.id);
      toast.success('Équipement supprimé');
      setDeleteOpen(false); setDeleteForm({});
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <PageLayout title="Administration — Équipements">
      <div className="px-4 pt-2 pb-2">
        <h1 className="text-xl font-semibold mb-3">Gestion des équipements</h1>
      </div>

      {/* Création */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)}>
        <form onSubmit={onCreateEquipement}>
          <h2 className="text-lg font-semibold mb-4">
            Nouvel équipement{' '}
            {createForm.etablissement_libelle && (
              <span className="text-sm bg-slate-100 px-2 py-0.5 rounded">{createForm.etablissement_libelle}</span>
            )}
          </h2>
          <EquipementForm form={createForm} onChange={changeCreate} />
          <div className="flex justify-end gap-2 mt-5">
            <Button type="button" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button type="submit">Créer</Button>
          </div>
        </form>
      </Modal>

      {/* Édition */}
      <Modal open={updateOpen} onClose={() => setUpdateOpen(false)}>
        <form onSubmit={onUpdateEquipement}>
          <h2 className="text-lg font-semibold mb-4">Modifier l'équipement</h2>
          <EquipementForm form={updateForm} onChange={changeUpdate} />
          <div className="flex justify-end gap-2 mt-5">
            <Button type="button" onClick={() => setUpdateOpen(false)}>Annuler</Button>
            <Button type="submit">Modifier</Button>
          </div>
        </form>
      </Modal>

      {/* Suppression */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <p className="mb-4">
          Supprimer <strong>{deleteForm.libelle}</strong> ?
          <br /><span className="text-sm text-gray-400">Cette action est irréversible.</span>
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={() => setDeleteOpen(false)}>Annuler</Button>
          <Button variant="danger" type="button" onClick={onDeleteEquipement}>Supprimer</Button>
        </div>
      </Modal>

      {loading ? (
        <p className="text-sm text-gray-500 px-4">Chargement…</p>
      ) : error ? (
        <p className="text-sm text-red-600 px-4">{error}</p>
      ) : (
        <div className="mt-3 space-y-2 px-4 pb-8">
          {entities.map((entity) => {
            const isOpen = openIds.has(entity.entite_id);
            return (
              <div key={entity.entite_id} className="bg-white rounded-xl shadow p-3">
                <button onClick={() => toggle(entity.entite_id)} className="w-full text-left flex items-center gap-2">
                  {isOpen ? <ChevronDown strokeWidth={3} size={18} className="text-slate-500" /> : <ChevronRight strokeWidth={3} size={18} className="text-slate-500" />}
                  <span className="font-medium">{entity.entite_libelle}</span>
                  <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-slate-200 text-slate-700 text-xs">
                    {entity.etablissements.length} établissement{entity.etablissements.length > 1 ? 's' : ''}
                  </span>
                </button>

                {isOpen && (
                  <div className="mt-3 space-y-3">
                    {entity.etablissements.map((etab) => {
                      const eqs = getEqs(etab);
                      return (
                        <div key={etab.etablissement_id} className="bg-gray-100 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium">
                              {etab.etablissement_libelle}
                              <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-slate-600 text-slate-50 text-xs ml-2">
                                {eqs.length} équipement{eqs.length > 1 ? 's' : ''}
                              </span>
                            </span>
                            <Button
                              variant="danger"
                              onClick={() => {
                                setCreateOpen(true);
                                setCreateForm({
                                  etablissement_id: etab.etablissement_id,
                                  etablissement_libelle: etab.etablissement_libelle,
                                  config: {},
                                });
                              }}
                            >
                              <CirclePlus size={18} className="inline-block" />
                              <span className="hidden md:inline ml-1">Ajouter un équipement</span>
                            </Button>
                          </div>

                          {eqs.length === 0 ? (
                            <p className="text-sm text-gray-400">Aucun équipement dans cet établissement</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              {eqs.map((eq) => (
                                <div key={eq.id} className="bg-gradient-to-r from-[#A51A1F] to-[#DB0E16] text-slate-50 rounded-xl p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="font-medium text-sm">{eq.libelle}</div>
                                      <div className="mt-1"><TypeBadge type={eq.type} /></div>
                                      {eq.ip && <div className="text-xs text-red-100 mt-1 font-mono">{eq.ip}</div>}
                                      {TYPES_HOME_ASSISTANT.includes(eq.type) && eq.config?.ha_entity_id && (
                                        <div className="text-xs text-amber-200 mt-1 font-mono truncate">{eq.config.ha_entity_id}</div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      {eq.type === 'imprimante-code-barre' && eq.ip && (
                                        <button
                                          onClick={() => testConnection(eq)}
                                          disabled={testingId === eq.id}
                                          className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
                                          title="Tester la connexion TCP"
                                        >
                                          <Wifi size={15} className={testingId === eq.id ? 'animate-pulse' : ''} />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => { setUpdateOpen(true); setUpdateForm({ id: eq.id, libelle: eq.libelle, ip: eq.ip || '', type: eq.type, config: eq.config || {} }); }}
                                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition"
                                      >
                                        <Edit size={15} />
                                      </button>
                                      <button
                                        onClick={() => { setDeleteOpen(true); setDeleteForm({ id: eq.id, libelle: eq.libelle }); }}
                                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition"
                                      >
                                        <Trash2 size={15} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageLayout>
  );
}
